import TradingPartnerResolver, { OrderChannels, TradingPartner } from './resolver/trading-partner-resolver';
import UtilCref from '@cewe-designsystem/util_cref';
import { isAffiliateSession } from '@cewe-designsystem/util_session';
import { getHpsKeyAccount, isMarketingSession } from './utils/utils';
import { ORDER_CHANNEL_KEY } from './types/orderChannels';

export interface WizardData {
  tradingPartners: TradingPartner[];
  orderChannels: OrderChannels | null;
  keyAccount: number;
}

export default class Wizard {
  private tradingPartnerResolver: TradingPartnerResolver;
  private keyAccount: number;
  private productId: number;
  private downloadPageLink: string;
  private mapKeyAccount: number | null;
  private overrideOwnIpsLink: string | null;
  private utilCref: UtilCref;

  constructor(
    tradingPartnerResolver: TradingPartnerResolver,
    keyAccount: number,
    productId: number,
    downloadPageLink: string,
    mapKeyAccount: number | null,
    overrideOwnIpsLink: string | null,
  ) {
    this.tradingPartnerResolver = tradingPartnerResolver;
    this.keyAccount = keyAccount;
    this.productId = productId;
    this.downloadPageLink = downloadPageLink;
    this.mapKeyAccount = mapKeyAccount;
    this.overrideOwnIpsLink = overrideOwnIpsLink;

    this.utilCref = new UtilCref(`${keyAccount}`);
  }

  async get(): Promise<WizardData> {
    let tradingPartners: TradingPartner[] = [];
    let keyAccount = this.keyAccount;

    if (!isMarketingSession() && !isAffiliateSession(this.keyAccount)) {
      tradingPartners = (await this.tradingPartnerResolver.resolve()).map((partner) => {
        partner.orderChannels = this.replaceOrderChannelCrefPlaceholders(partner.orderChannels);
        if (partner.orderChannels.software?.type == ORDER_CHANNEL_KEY.DESKTOP_SOFTWARE) {
          partner.orderChannels.software.link = this.getHpsCtaLink(partner.tpAccountId, this.productId);
        }
        return partner;
      });
    }

    const keyAccountOrderChannels = await this.tradingPartnerResolver.getKeyAccountOrderChannels(this.keyAccount);
    if (keyAccountOrderChannels?.software?.type === ORDER_CHANNEL_KEY.DESKTOP_SOFTWARE) {
      keyAccount = getHpsKeyAccount(this.keyAccount, this.mapKeyAccount);
      keyAccountOrderChannels.software.link = this.getHpsCtaLink(keyAccount, this.productId);
    }

    // when order channel online doesnt exist it is blocked so dont set it
    if (keyAccountOrderChannels?.online && this.overrideOwnIpsLink) {
      keyAccountOrderChannels.online = this.overrideOwnIpsLink;
    }

    return {
      orderChannels: keyAccountOrderChannels ? this.replaceOrderChannelCrefPlaceholders(keyAccountOrderChannels) : null,
      tradingPartners: tradingPartners,
      keyAccount: keyAccount,
    };
  }

  private getHpsCtaLink(keyAccountId: number, productId: number): string {
    const url = new URL(this.downloadPageLink, window.location.href);
    const searchParams = new URLSearchParams({
      keyAccount: `${keyAccountId}`,
      product: `${productId}`,
    });

    return `${url}?${searchParams.toString()}`;
  }

  private replaceOrderChannelCrefPlaceholders(orderChannels: OrderChannels): OrderChannels {
    if (orderChannels.online) {
      // replace optional CREF Placeholder in URL and append cref (short version) as query param
      if (orderChannels.online.startsWith('http')) {
        try {
          const onlineUrl = new URL(this.utilCref.replacePlaceholder(orderChannels.online));
          const urlParams = new URLSearchParams(onlineUrl.search);
          urlParams.append('cref', this.utilCref.getShortCref());
          onlineUrl.search = urlParams.toString();
          orderChannels.online = onlineUrl.toString();
        } catch (error) {
          console.error(error, 'fallback to un-cref-ed version');
          orderChannels.online = this.utilCref.replacePlaceholder(orderChannels.online);
        }
      } else {
        orderChannels.online = this.utilCref.replacePlaceholder(orderChannels.online);
      }
    }

    if (orderChannels.software) {
      orderChannels.software.link = this.utilCref.replacePlaceholder(orderChannels.software.link);
    }

    return orderChannels;
  }
}
